import { Container, Col, Row } from 'react-bootstrap';
import translationFallback from '@/utilites/translations/translationFallback';
import { useTranslation } from 'next-i18next';
import Head from 'next/head';
import BreadcrumbStructuredData from '@/components/structured_data/BreadcrumbStructuredData';
import { useRouter } from 'next/router';
import getBasicStaticSideProps from '@/lib/staticSideProps/getBasicStaticSideProps';
import getCanonicalUrl from '@/utilites/seo/get-canonical-url';
import LanguageAlternates from '@/components/seo/LanguageAlternates';

export default function Terms() {
  const { t } = useTranslation('other');
  const { t: tCommon } = useTranslation('common');
  const router = useRouter();
  const canonicalUrl = getCanonicalUrl(router);

  return (
    <>
      <Head>
        <title>{t('terms.meta_title')}</title>
        <link rel="canonical" href={canonicalUrl} />
        <LanguageAlternates />
        <BreadcrumbStructuredData
          items={[
            { name: 'Home', item: 'https://spiritory.com' },
            {
              name: translationFallback(
                'terms.terms_and_conditions',
                t('terms.terms_and_conditions'),
                'Terms and Conditions'
              ),
              item: canonicalUrl,
            },
          ]}
        />
      </Head>
      <Container className="p-5">
        <Row
          className="bg-primary text-white rounded"
          style={{
            width: '60%',
            minWidth: 900,
            padding: '1.5rem 4rem',
            margin: '0 auto',
          }}
        >
          <Row>
            <Col className="d-flex justify-content-center align-items-center">
              <h1>
                {translationFallback(
                  'terms.terms_and_conditions',
                  t('terms.terms_and_conditions'),
                  'Terms and Conditions'
                )}
              </h1>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex flex-column gap-3 mt-4">
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.first_header',
                    t('terms.first_header'),
                    '1. Our Service'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.first_paragraph',
                    t('terms.first_paragraph'),
                    'Spiritory (or "we") provides an investment platform enabling you to invest in spirits, wine, and champagne. Through our stock market-like bidding and offering model, you can buy or sell items. Anyone using our services is referred to as a user (or "you"). Users placing a bid or purchasing an item are referred to as "Buyers". Users creating an offer or selling an item are termed "Sellers". By using Spiritory, you agree that we provide our services to both Buyers and Sellers. Please note:'
                  )}
                </p>
                <ol>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.first_list.first_item.strong',
                        t('terms.first_list.first_item.strong'),
                        'Spiritory has an age restriction for users.'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.first_list.first_item.text',
                      t('terms.first_list.first_item.text'),
                      'Due to the nature of the products sold on Spiritory, you must be at least 18 years old to use the service. By using Spiritory, you confirm that you are 18 years of age or older. As part of the registration process and at all other stages, especially when buying, selling, or receiving products, we may ask you to confirm your age.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.first_list.second_item.strong',
                        t('terms.first_list.second_item.strong'),
                        'Use of our Service:'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.first_list.second_item.text',
                      t('terms.first_list.second_item.text'),
                      'Our website and service are intended for use by private individuals and not for use by (professional) companies. Only users residing in Germany have access to all functionalities. Users from other countries can create an account but are not permitted to place bids, buy items, create offers, or sell items.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.first_list.third_item.strong',
                        t('terms.first_list.third_item.strong'),
                        'Spiritory is not a party to the purchase contract'
                      )}
                    </strong>
                    {translationFallback(
                      'terms.first_list.third_item.text',
                      t('terms.first_list.third_item.text'),
                      ' Spiritory is the owner of the website and provides the services described in these Terms and Conditions. However, we are neither the buyer nor the seller of items, nor are we owners of items, nor are we legally in possession of products. Our role regarding the sale and purchase of items is limited to facilitating transactions for products between buyers and sellers, as outlined in these Terms and Conditions.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.first_list.fourth_item.strong',
                        t('terms.first_list.fourth_item.strong'),
                        'Spiritory charges a fee for the use of our services'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.first_list.fourth_item.text',
                      t('terms.first_list.fourth_item.text'),
                      "For the use of our services, we charge fees for both the buyer and the seller. The fee for the buyer ('processing fee') is 6% of the purchase price (including VAT, if applicable), along with a €6 shipping cost. We will add the processing fee and shipping cost to your bid price. When you place a bid, we'll provide you with an overview of potential fees and also send you an email after you've placed a bid, edited a bid, or purchased an item. The fee for the seller (\"commission fee\") is 6% of the sales price (including VAT, if applicable), and 3% for payment processing. The seller fees are deducted from the selling price. When you create an offer, we\"ll provide you with a fee overview and also send you an email when you submit an offer, edit an offer, or sell an item. You agree that the fee is non-refundable. The use of our dashboard, portfolio functionality, and access to performance and market data is free but requires an individual account. If additional taxes are due in connection with the purchase or sale of an item, the buyer or seller is responsible for remitting the taxes to the relevant authorities. Spiritory may occasionally offer discounts and promotional codes, which users will be informed about via email."
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.first_list.fifth_item.strong',
                        t('terms.first_list.fifth_item.strong'),
                        'Spiritory engages third-party providers.'
                      )}
                    </strong>
                    {translationFallback(
                      'terms.first_list_fifth_item.text',
                      t('terms.first_list_fifth_item.text'),
                      ' We aim to provide you with the best possible user experience. For this reason, we utilize third-party providers, such as payment and shipping services. Our website may contain links to these third-party websites. Your use of products and services offered by these providers may be subject to their terms of use and/or privacy policies, which govern their services.'
                    )}
                  </li>
                </ol>
                <br />
                {translationFallback(
                  'terms.first_list.end_list',
                  t('terms.first_list.end_list'),
                  'Additionally, Spiritory does not guarantee that: (i) an item will be sold; (ii) an item listed for sale exists; (iii) a seller is able to sell the listed item; (iv) a buyer is able to purchase and pay for items; (v) a buyer will return a product if requested; or (vi) a buyer or seller will complete a transaction.'
                )}
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.second_header',
                    t('terms.second_header'),
                    '2. Your Spiritory account, deposits, and withdrawals'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.second_paragraph',
                    t('terms.second_paragraph'),
                    "To utilize our investment platform, you need to create an 'account' with us. It is mandatory to use a valid email, provide your full name, enter a secure password, and provide us with your date of birth. If you wish to update your personal information, you can change your password anytime by logging into your account."
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.third_paragraph',
                    t('terms.third_paragraph'),
                    ' When creating your account, you must provide us with accurate information. This means providing correct and up-to-date details about yourself and truthfully filling in all required fields. You must not create an account using a false identity, incorrect age, or false credit card information. Only accurate information helps us provide you with an excellent user experience. By agreeing, you take full responsibility for all activities occurring under your account. Furthermore, your account and username must be appropriate and should not contain misleading, offensive, or vulgar information.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.fourth_paragraph',
                    t('terms.fourth_paragraph'),
                    'We ask you to protect your account by choosing a secure password. You are responsible for the confidentiality of your password and account. Never disclose your account details, including the password, to other individuals.'
                  )}
                </p>
                <p>
                  <strong>
                    {translationFallback(
                      'terms.fifth_paragraph.strong',
                      t('terms.fifth_paragraph.strong'),
                      'If you violate our Terms of Service, we reserve the right to restrict or delete your account entirely.'
                    )}
                  </strong>
                  {translationFallback(
                    'terms.fifth_paragraph.text_1',
                    t('terms.fifth_paragraph.text_1'),
                    "In such a case, you are not allowed to continue using our services, nor are you permitted to open a new account or use someone else's account. We reserve the right to involve authorities such as the police if we suspect fraudulent or other unlawful behavior and activities."
                  )}
                  <br />
                  {translationFallback(
                    'terms.fifth_paragraph.text_2',
                    t('terms.fifth_paragraph.text_2'),
                    'We hope that you enjoy our website and our services, but you can cancel your account at any time.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.sixth_paragraph',
                    t('terms.sixth_paragraph'),
                    " To place bids or buy items on Spiritory, you must have a valid debit, credit, or other payment method approved by us, stored with our external payment service provider. To place bids for products on the website, we require bank details such as IBAN, which are stored with our third-party payment service provider. Our third-party payment processor may change the payment methods at any time. If a payment method stored by our payment processor for you is no longer acceptable, you will be notified by us so that you can provide the payment processor with a new acceptable payment method. You can change your payment method at any time, provided that the new payment method is acceptable to our third-party payment processor. We reserve the right to suspend or close any account at our discretion if we are notified by our payment service provider that your payment method is not valid or if they suspect fraudulent behavior. You agree to provide our third-party providers with a valid payment method and authorize them to automatically charge your payment method when making a purchase, in accordance with these Terms and Conditions. If you are a seller, you agree that we will typically initiate payouts 5 to 7 business days after the package has arrived at the buyer's location."
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.third_header',
                    t('terms.third_header'),
                    '3. Website Content'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.seventh_paragraph',
                    t('terms.seventh_paragraph'),
                    'All content related to the products on the website (including images, product descriptions, product specifications) is provided by Spiritory and is the sole content used in relation to a product. Spiritory reserves the right to modify the content at any time at its own discretion. While we strive to provide reliable and accurate content regarding the products, we do not guarantee the accuracy of the content we publish on the website about a product. You cannot hold Spiritory responsible for inaccuracies in the content, even if you have alerted us to any inaccuracy at any point. Although we aim to create content for a product, if you inform us that no content is available on our website, you cannot hold Spiritory responsible if we have no detailed content or any content at all for a product, even if you have requested it.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.eighth_paragraph',
                    t('terms.eighth_paragraph'),
                    'The website, product details, and the contents contained on the website include images, product information, data, and intellectual property that are owned by Spiritory and third parties. Accordingly, you are not permitted to copy content from the website, reproduce content from the website (except in accordance with these terms), provide links to third-party websites, or otherwise use the content without our prior written consent, which shall be solely at our discretion.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.ninth_paragraph',
                    t('terms.ninth_paragraph'),
                    ' You are not permitted to use any part of the content from our website for commercial purposes without obtaining written consent from us, which shall be solely at our discretion. If you print, copy, or download any part of our website and breach these terms of use, your right to use our website shall cease immediately, and at our discretion, you must return or destroy any copies of the materials you have created.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.fourth_header',
                    t('terms.fourth_header'),
                    '4. Product Authentication'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.tenth_paragraph',
                    t('terms.tenth_paragraph'),
                    "Spiritory verifies each product sold on Spiritory for obvious signs of damage and the product goes through our authentication process. The product will only be shipped to the buyer if it has passed the authenticity verification process (and has been fully paid for by the buyer). If Spiritory determines that the item does not pass the authenticity check (which is at Spiritory's discretion), the item will be returned to the seller, and the seller is obligated to reimburse Spiritory for the return costs of the product. The payment made by the buyer will be refunded by Spiritory to the buyer. The product authentication service is a voluntary, non-commercial offering by Spiritory. We are not liable for misjudgments or malicious behavior by users (especially sellers)."
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.fifth_header',
                    t('terms.fifth_header'),
                    '5. Counterfeit Products'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.eleventh_paragraph',
                    t('terms.eleventh_paragraph'),
                    'Spiritory takes counterfeiting and fraud very seriously. While we hope that counterfeit products are detected through our product authentication, we are aware that no procedure is foolproof. If a buyer receives a product they believe to be a counterfeit, they must inform Spiritory immediately in writing. We will then instruct you to return the product to us for examination.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twelfth_paragraph',
                    t('terms.twelfth_paragraph'),
                    "If a seller offers a counterfeit item or attempts to deceive a buyer or Spiritory, or if we suspect fraud or a counterfeit product, Spiritory reserves the right to suspend or close a seller's account at its discretion, cancel all or part of the seller's outstanding orders (including any outstanding orders where the seller is also acting as a buyer), withhold any due payments to the seller, return the item at the seller's expense, charge the seller for any costs incurred by the buyer in returning the product to Spiritory, and report all counterfeit items and details of any suspected fraud to the police and/or other relevant authorities. If the buyer has paid for the product(s) in question, which turns out to be counterfeit, the buyer will receive a full refund within 14 days of the completion of the investigation. Spiritory reserves the right to monitor the Spiritory trading platform and investigate and/or close a member's account if there is suspicion of fraudulent activities or market manipulation of any kind, reporting such activities to the police or other relevant law enforcement agencies."
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.sixth_header',
                    t('terms.sixth_header'),
                    '6. Seller’s Obligations'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.thirteenth_paragraph',
                    t('terms.thirteenth_paragraph'),
                    'The seller agrees to be bound by all the terms and conditions contained in these Terms that apply to sellers. If you list an offer for a product on the website, you do so on the basis that you are making a binding offer to sell that product to a successful buyer. You are contractually obligated to sell the product to the buyer and ship it to the buyer once the product has passed our review process and has been paid for. You understand that your contract for the sale of a product is with the buyer and not with Spiritory and that Spiritory is merely acting as an intermediary in the sale of the product in accordance with these Terms. Accordingly, Spiritory, subject to these Terms, makes no representations and accepts no liability to a seller in relation to the transaction with a buyer. A seller may cancel a request at any time before that request is matched with a bid from a buyer.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.fourteenth_paragraph',
                    t('terms.fourteenth_paragraph'),
                    ' Sellers must ensure that they ship the exact product for which they created an offer and for which a successful bid was placed on the website. If Spiritory, at its sole discretion, concludes that the shipped product does not match the product description on the website, is not authentic, or is not in good condition (taking into account the review process), Spiritory is entitled to charge the seller the fees set forth in these Terms and to take the actions set forth in these Terms (which may include pausing or terminating your account).'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.fifteenth_paragraph',
                    t('terms.fifteenth_paragraph'),
                    " You must ship the sold products to the customer within 3 business days after our email to you confirming authentication and/or the buyer's payment. If you are a business, you agree to the terms in these terms and conditions regarding the buyer's right to return the products. In any case, whether you are a business or not, you agree that Spiritory's discretion is final when deciding whether the buyer can return a product or is eligible for a full or partial refund. Additionally, you agree to pay Spiritory all fees and shipping costs determined by Spiritory as payable by you in accordance with these terms and conditions."
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.sixteenth_paragraph',
                    t('terms.sixteenth_paragraph'),
                    'Spiritory will make a payout to the seller within a reasonable period after successful verification of the product. Typically, the payout occurs 5 to 7 days after the estimated delivery date. However, if a customer complains about the received product or if we encounter issues with other items offered by the seller, this timeframe may be extended. As a reminder: You are not allowed to contact buyers outside of Spiritory to (i) purchase directly from you/your online shop, (ii) agree to different terms or arrangements, and/or (iii) request additional payments.>'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.seventh_header',
                    t('terms.seventh_header'),
                    "7. The Buyer's Obligations"
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.seventeenth_paragraph',
                    t('terms.seventeenth_paragraph'),
                    'The Buyer agrees to be bound by all provisions and conditions contained in these terms that apply to buyers. A buyer may place a bid on an item offered for sale through the website. If you win an auction in accordance with these terms, you are contractually obligated to pay the agreed-upon price for the product. A buyer may cancel a bid at any time before that bid is matched with an offer. You acknowledge that your contract for the sale of a product is with the seller and not with Spiritory, and that Spiritory merely acts as an intermediary in the sale of the product in accordance with these terms. Accordingly, subject to these terms, Spiritory makes no representations and assumes no liability to buyers in connection with the transaction with a seller.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.eighteenth_paragraph',
                    t('terms.eighteenth_paragraph'),
                    'You have the right to return a product in accordance with these terms, but you must adhere to all requirements outlined in these terms regarding the return of a product. You acknowledge that your right to cancel an order or return a product is at the absolute discretion of Spiritory, that any disputes regarding a product will be resolved between the buyer and the seller, and that any intervention in such a dispute is solely at the discretion of Spiritory.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.eighth_header',
                    t('terms.eighth_header'),
                    '8. Disputes with Other Users'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.ninetheen_paragraph',
                    t('terms.ninetheen_paragraph'),
                    'The users acknowledge that the contract for the sale of items is made between the buyer and the seller. Therefore, we expect users to resolve any disputes among themselves. Spiritory may intervene in mediation between the buyer and the seller. To allow us the opportunity to assist you, all complaints and disputes between the seller and the buyer must be reported to us as soon as possible after the issue arises. Even with our support, it is possible that disputes may not be resolved to the satisfaction of the user. Users agree that in such cases, we may, at our discretion and considering all available information and in accordance with our Terms of Service, decide to: (i) compensate the buyer or (ii) pay the seller. Spiritory cannot be compelled to compensate the buyer and the seller in connection with our decision. Beyond our decision, buyers and sellers have the opportunity to discuss their claims related to a purchase contract among themselves. We reserve the right not to refund fees such as purchase and sales fees, including payment processing fees and fees for the shipping label(s), if the buyer and the seller decide to terminate the purchase contract without our involvement.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.ninth_header',
                    t('terms.ninth_header'),
                    '9. Disputes with Spiritory'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.twentieth_paragraph',
                    t('terms.twentieth_paragraph'),
                    'If you have a reason to complain about our services, please let us know, and we will try to resolve your issue. Please direct all complaints to the address provided at the end of these Terms and Conditions. While we prefer to address all issues ourselves, we would like to inform you that you also have the option to submit a complaint to an out-of-court dispute resolution authority (further information can be found on the website of the Online Dispute Resolution platform). If we are unable to find a solution, please refer to the section on Applicable Law.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.tenth_header',
                    t('terms.tenth_header'),
                    '10. Liability'
                  )}
                </strong>
                <div>
                  <p>
                    {translationFallback(
                      'terms.twenty_first_paragraph',
                      t('terms.twenty_first_paragraph'),
                      'Nothing in these Terms and Conditions excludes our liability for death or personal injury arising from our negligence, or our liability for fraud or fraudulent misrepresentation, or any other liability that cannot be excluded or limited under German law. To the extent permitted by law, we exclude all express or implied conditions, warranties, representations, or other terms that may apply to our website or its contents. We shall not be liable to any user for any loss or damage, whether in contract, tort (including negligence), breach of statutory duty, or otherwise, even if foreseeable, arising under or in connection with:'
                    )}
                  </p>
                  <ul>
                    <li>
                      {translationFallback(
                        'terms.second_list.first_item',
                        t('terms.second_list.first_item'),
                        'the use or inability to use our website or services; or'
                      )}
                    </li>
                    <li>
                      {translationFallback(
                        'terms.second_list.second_item',
                        t('terms.second_list.second_item'),
                        'the use of or reliance on any content displayed on our website.'
                      )}
                    </li>
                  </ul>
                </div>
                <div>
                  <p>
                    {translationFallback(
                      'terms.twenty_second_paragraph',
                      t('terms.twenty_second_paragraph'),
                      'Please note that we provide our website solely for domestic and private use. You agree not to use our website for any commercial or business purposes, and in any event, we shall have no liability to you for'
                    )}
                    :
                  </p>
                  <ul>
                    <li>
                      {translationFallback(
                        'terms.third_list.first_item',
                        t('terms.third_list.first_item'),
                        'loss of profits, sales, business, or revenue;'
                      )}
                    </li>
                    <li>
                      {translationFallback(
                        'terms.third_list.second_item',
                        t('terms.third_list.second_item'),
                        'business interruption;'
                      )}
                    </li>
                    <li>
                      {translationFallback(
                        'terms.third_list.third_item',
                        t('terms.third_list.third_item'),
                        'loss of anticipated savings;'
                      )}
                    </li>
                    <li>
                      {translationFallback(
                        'terms.third_list.fourth_item',
                        t('terms.third_list.fourth_item'),
                        'loss of business opportunities, goodwill, or reputation; or'
                      )}
                    </li>
                    <li>
                      {translationFallback(
                        'terms.third_list.fifth_item',
                        t('terms.third_list.fifth_item'),
                        'indirect or consequential loss.'
                      )}
                    </li>
                  </ul>
                </div>
                <p>
                  {translationFallback(
                    'terms.twenty_third_paragraph',
                    t('terms.twenty_third_paragraph'),
                    ' Spiritory shall not be liable for incorrectly calculated performance data such as market value, returns, other trends, or any other inaccurate metrics such as sales price or release date.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twenty_fourth_paragraph',
                    t('terms.twenty_fourth_paragraph'),
                    ' Spiritory reserves the right, at any time and without prior notice, to make technical, commercial, or other changes and/or improvements to our website. We also reserve the right to (i) temporarily suspend our website and/or restrict the use of our services when necessary (e.g., due to necessary maintenance of our website), or (ii) in the event of force majeure. In both cases, Spiritory is exempt from any liability for damages and from any obligation to provide compensation (in any form).'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twenty_fifth_paragraph',
                    t('terms.twenty_fifth_paragraph'),
                    '  We are not liable for any losses or damages caused by bugs, viruses, or other technologically harmful materials that may infect your computer equipment, computer programs, data, or other proprietary material due to the use of our website or downloading content from it or any website linked to it. We do not take responsibility for the content of websites linked to our site. We are not liable for any losses or damages resulting from your use of these websites.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twenty_sixth_paragraph',
                    t('terms.twenty_sixth_paragraph'),
                    'Spiritory is not liable to the buyer for acts or omissions of the seller and is not liable to the buyer for acts or omissions of the seller.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twenty_seventh_paragraph',
                    t('terms.twenty_seventh_paragraph'),
                    ' We are not liable for product verification. This is a voluntary, non-commercial offering by Spiritory to prevent counterfeiting of spirits and wine products. We commit to verifying every product traded on Spiritory. However, no process is immune to fraud and bad actors.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twenty_eighth_paragraph',
                    t('terms.twenty_eighth_paragraph'),
                    'Spiritory is not liable for the content of bottles or if an item has gone bad.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.twenty_ninth_paragraph',
                    t('terms.twenty_ninth_paragraph'),
                    '  Insofar as Spiritory is liable to buyers, sellers, or other users of the website or services, the liability of Spiritory, to the extent permitted by law, is limited to an amount of €250 (two hundred and fifty euros).'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.eleventh_header',
                    t('terms.eleventh_header'),
                    '11. Privacy Policy and Your Personal Data'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.thirty_paragraph',
                    t('terms.thirty_paragraph'),
                    'The use of your personal data that you submit to or through the website is governed by our Privacy Policy. You can find details on how we handle your personal data in our Privacy Policy. We will only use your personal data in accordance with our Privacy Policy. Please take the time to read it carefully as it contains important information on how we collect and use personal data.'
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.thirty_first_paragraph',
                    t('terms.thirty_first_paragraph'),
                    "User anonymity is important to us, and we strive to ensure that a seller's identity is not disclosed to the buyer unless we are legally obliged to do so. In order to enable sellers to ship items, we provide the buyer's address details to the sellers, but only after a product has undergone the authentication process and has been paid for by the buyer. Sellers are not authorized to contact buyers for commercial purposes."
                  )}
                </p>
                <p>
                  {translationFallback(
                    'terms.thirty_second_paragraph',
                    t('terms.thirty_second_paragraph'),
                    'If you are a business, these terms, their subject matter, and their formation (as well as any non-contractual disputes or claims) are governed by German law. You agree to the exclusive jurisdiction of the courts in Germany. In the event of any inconsistency between the German version of our Terms and Conditions and any other language version, the German version shall prevail.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.twelfth_header',
                    t('terms.twelfth_header'),
                    '12. Changes to the Terms and Conditions'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.thirty_third_paragraph',
                    t('terms.thirty_third_paragraph'),
                    "We reserve the right to amend or supplement the Terms and Conditions at any time. The current version of Spiritory's terms and conditions can be found on our website. If a change or addition significantly affects your rights or obligations, we will notify you by email or make you aware of the changes during your use of our platform. If you continue to use our platform after an amendment or addition to the terms and conditions, you irrevocably accept the amended or supplemented terms and conditions."
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.thirteenth_header',
                    t('terms.thirteenth_header'),
                    '13. Severability'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.thirty_fourth_paragraph',
                    t('terms.thirty_fourth_paragraph'),
                    'If one or more provisions of the Terms and Conditions are, for any reason, found to be wholly or partially invalid or unenforceable, the user and Spiritory remain bound by the remaining Terms and Conditions. We will replace the invalid and/or unenforceable part (with respect to the specific user or situation) with one or more provisions that are valid. These new provision(s) will have legal effects that correspond as closely as possible to the ineffective provision(s), based on the content and purpose of the Terms and Conditions.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.return_policy.header',
                    t('terms.return_policy.header'),
                    'Return & Refund Policy'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.return_policy.intro',
                    t('terms.return_policy.intro'),
                    'This Return & Refund Policy explains when and how returns are accepted on Spiritory and how refunds are processed in order to comply with Google Merchant requirements.'
                  )}
                </p>
                <ul>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.return_policy.window.strong',
                        t('terms.return_policy.window.strong'),
                        'Return window'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.return_policy.window.text',
                      t('terms.return_policy.window.text'),
                      'Unless otherwise stated by the seller or required by law, buyers can request a return within 14 days after delivery. Business sellers may offer a longer period; if so, it will be shown during checkout.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.return_policy.condition.strong',
                        t('terms.return_policy.condition.strong'),
                        'Item condition'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.return_policy.condition.text',
                      t('terms.return_policy.condition.text'),
                      'Items must be returned in the same condition as delivered, unopened and with all original accessories and packaging. Tamper-evident seals must be intact. Opened or consumed goods cannot be returned unless they are defective as defined by applicable law.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.return_policy.process.strong',
                        t('terms.return_policy.process.strong'),
                        'How to start a return'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.return_policy.process.text',
                      t('terms.return_policy.process.text'),
                      'Start your return from your Spiritory account within the return window. We will provide a return label and instructions after review.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.return_policy.shipping.strong',
                        t('terms.return_policy.shipping.strong'),
                        'Return shipping costs'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.return_policy.shipping.text',
                      t('terms.return_policy.shipping.text'),
                      'If the return is due to an error, damage, or incorrect item, the seller bears the return costs. For buyer’s remorse returns, the buyer pays the return shipping unless otherwise required by local consumer law.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.return_policy.refunds.strong',
                        t('terms.return_policy.refunds.strong'),
                        'Refunds'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.return_policy.refunds.text',
                      t('terms.return_policy.refunds.text'),
                      'Refunds are issued to the original payment method within 5–7 business days after the item is received and inspected. Original outbound shipping and processing fees are refunded only if the return is due to an error, damage, or legal warranty.'
                    )}
                  </li>
                  <li>
                    <strong>
                      {translationFallback(
                        'terms.return_policy.exceptions.strong',
                        t('terms.return_policy.exceptions.strong'),
                        'Exceptions'
                      )}
                    </strong>{' '}
                    {translationFallback(
                      'terms.return_policy.exceptions.text',
                      t('terms.return_policy.exceptions.text'),
                      'Returns may be refused for items returned used, opened, or damaged by the buyer, for hygiene/safety reasons, or where prohibited by law. Your statutory rights remain unaffected.'
                    )}
                  </li>
                </ul>
                <p>
                  {translationFallback(
                    'terms.return_policy.contact',
                    t('terms.return_policy.contact'),
                    'Need help with a return? Contact us using the details below.'
                  )}
                </p>
              </div>
              <div className="d-flex flex-column gap-1">
                <strong style={{ fontSize: '1.5rem' }}>
                  {translationFallback(
                    'terms.fourteenth_header',
                    t('terms.fourteenth_header'),
                    '14. Contact'
                  )}
                </strong>
                <p>
                  {translationFallback(
                    'terms.thirty_fifth_paragraph',
                    t('terms.thirty_fifth_paragraph'),
                    'If you have any questions regarding our Terms and Conditions or our website and its services, you can contact us using the following contact information:'
                  )}
                </p>
                <ul>
                  <li>SPIRITORY GmbH</li>
                  <li>Oberländerstr. 33</li>
                  <li>
                    81371{' '}
                    {translationFallback(
                      'terms.munich',
                      t('terms.munich'),
                      'Munich'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'terms.germany',
                      t('terms.germany'),
                      'Germany'
                    )}
                  </li>
                  <li>
                    {translationFallback(
                      'phone_number',
                      tCommon('phone_number'),
                      'Phone number'
                    )}
                    : +49 15739597262
                  </li>
                  <li>
                    {translationFallback('email', tCommon('email'), 'Email:')}:{' '}
                    <a className="text-white" href="mailto:help@spiritory.com">
                      info [at] spiritory.com
                    </a>
                  </li>
                </ul>
              </div>
              <p>
                {translationFallback(
                  'terms.updated_at',
                  t('terms.updated_at'),
                  'These Terms and Conditions were last updated on'
                )}{' '}
                <strong>January 29, 2022</strong>.
              </p>
            </Col>
          </Row>
        </Row>
      </Container>
    </>
  );
}

export const getStaticProps = getBasicStaticSideProps([
  'common',
  'other',
  'index',
]);
